/* eslint-disable no-nested-ternary */
/* eslint-disable no-debugger */
import Button from "@Components/Button";
import LeadFormDispatcher from "@Components/LeadFormModal/actions";
import handleLeadAPICall from "@Components/LeadFormV2/utils/handleLeadAPICall";
import { useFeatureIsOn, useFeatureValue } from "@growthbook/growthbook-react";
import PropertyPageDispatcher from "@Pages/PropertyPage/data/actions";
import useSearchList from "@Pages/Search/components/SearchList/useSearchList";
import { AppState } from "@Store/index";
import formatClassNames from "@Utils/clientUtils/stringUtility/formatClassNames";
import isPaymentActive from "@Utils/isPaymentActive";
import { runTasks } from "@Utils/performanceUtils";
import viewTransitionNavUtility from "@Utils/viewTransitionNavUtility";
import { GROWTH_BOOK_FEATURE_FLAG_NAMES } from "constants/index";
import { monthlyCountriesPriceDurationGaurd, PAGE } from "constants/configConstants";
import gtagEventEmitter, { gtagDuplicateEventEmitter } from "helpers/gtagEventEmitter";
import { useAnalytics } from "helpers/analytics";
import useIsDesktop from "hooks/useIsDesktop";
import useIsHappyFlow from "hooks/useIsHappyFlow";
import useIsHappyFlowBookForm from "hooks/useIsHappyFlowBookForm";
import ArrowIcon from "icons/ArrowIcon";
import { MouseEventHandler, useMemo, useState } from "react";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import { useHistory } from "hooks/useHistory";
import { getLink, getPricingCurrency, getPricingDuration, getPropertyFilter } from "../../helper";
import styles from "./BottomSection.module.scss";
import useTranslationHook from "hooks/useTranslationHook";
import wrapperHOC from "@Utils/wrapperHOC";
import useWhitelabel from "hooks/useWhitelabel";
import { convertToWhitelabelURL } from "@Pages/WhitelabelHome/Desktop/helpers/WhiteLabelRedirectionHelper";
import PropertyTags from "../PropertyTags";
import useIsApp from "hooks/useIsApp";
import navigateTo from "@Utils/webApputils/navigateTo";
import config from "@Config/index";
import Skeleton from "@Components/Skeleton";
import handleRedirectToPropertyPage from "@Components/LeadFormV2/utils/handleRedirectToPropertyPage";
import useCustomWindowOpen from "@Components/CustomWindowOpen";
import { useIsIframe } from "hooks/useIsIframe";
import { getIframeSourceParam } from "@Utils/iframeContext";
import { getIframeEventDataParam } from "@Utils/iframeEventData";
import { shouldShowSlashedPricing } from "helpers/slashedPricingHelper";

interface Props {
  pricing: any;
  meta: any;
  item: any;
  wrapRef?: any;
  page: string;
  isShortlistPage: boolean;
  name: any;
  location: any;
  canonical_name: any;
  propertyLink: any;
  showViewProperty?: boolean;
  showEnquireButton?: boolean;
  className?: string;
  showWeChatButton?: boolean;
  wrapperClassName?: string;
  showPropertyCardTag?: boolean;
}
const BottomSection = (props: Props) => {
  const customWindowOpen = useCustomWindowOpen();
  const [isLoading, setIsLoading] = useState(false);
  const {
    pricing,
    meta,
    item,
    wrapRef,
    page,
    isShortlistPage,
    name,
    location,
    canonical_name,
    propertyLink,
    showViewProperty = false,
    showEnquireButton = true,
    className,
    wrapperClassName = "",
    showPropertyCardTag,
  } = props;
  let redirectionLink = propertyLink?.includes("&regionId=undefined")
    ? propertyLink.replace("&regionId=undefined", "")
    : propertyLink;
  const dispatch = useDispatch();
  const history = useHistory();
  const isDesktop = useIsDesktop();

  const { userData } = useSelector(
    ({ appData }: AppState) => ({
      userData: appData.userData,
    }),
    shallowEqual,
  );

  const { trackEnquireNow } = useAnalytics();

  const { isLFOneFilled, personalDetails, isLeadFormFilled, enquired, region, redirection_urls } =
    useSelector(
      ({ leadFormModal, userHistory, searchPageData }: AppState) => ({
        isLFOneFilled: leadFormModal.isLFOneFilled,
        personalDetails: leadFormModal.personalDetails,
        isLeadFormFilled: leadFormModal.isLeadFormFilled,
        enquired: userHistory.enquired,
        region: searchPageData.region,
        redirection_urls: userHistory.redirection_urls,
      }),
      shallowEqual,
    );

  const { pricingPrice, pricingDuration } = monthlyCountriesPriceDurationGaurd({
    pricing,
    region,
    duration: getPricingDuration(pricing?.duration),
  });
  const { filters } = useSearchList();
  const isHappyFlowFromHook = useIsHappyFlow();
  const isHappyFlowBookForm = useIsHappyFlowBookForm();
  const { isWhitelabel, whitelabelColors, isDemoSubdomain, partner_uuid } = useWhitelabel();
  const { isIframe, iframeSource } = useIsIframe();
  const isHappyFlow = isHappyFlowFromHook || page === PAGE.ACTIVITY;
  const isEnquired = useMemo(() => enquired?.includes(item.id), [enquired?.length, item?.id]);
  const isPaymentBeforeBookform =
    useFeatureIsOn(GROWTH_BOOK_FEATURE_FLAG_NAMES.ENABLE_PAYMENT_BEFORE_BF) &&
    item?.payment_before_bookform &&
    isPaymentActive(item);
  const isZendeskFlowAfterLead = useFeatureIsOn(
    GROWTH_BOOK_FEATURE_FLAG_NAMES.POST_LEAD_SUBMISSION_ZENDESK_FLOW,
  );
  const openLeadModalFromBookCTA = useFeatureIsOn(
    GROWTH_BOOK_FEATURE_FLAG_NAMES.OPEN_LEAD_MODAL_FROM_BOOK_CTA,
  );
  const isApplyNowCTA = useFeatureIsOn(GROWTH_BOOK_FEATURE_FLAG_NAMES.APPLY_NOW_CTA);
  const isSlashedPricingEnabled = useFeatureIsOn(GROWTH_BOOK_FEATURE_FLAG_NAMES.SLASHED_PRICING);
  const showSlashPricing = shouldShowSlashedPricing({
    growthBookEnabled: isSlashedPricingEnabled,
    location: item?.location,
    providerId: item?.provider_id,
  });
  const defaultCTATextWithExperiment = ["Enquire", "See Availability", "View Property"];
  const searchPageCtaExperimentValue = useFeatureValue(
    GROWTH_BOOK_FEATURE_FLAG_NAMES.SEARCH_PROPERTY_PAGE_CTA_EXPERIMENT_VALUE,
    config.IS_CHINA_APP ? 0 : -1,
  );

  const { t } = useTranslationHook();

  const bookingsPageLink = isWhitelabel
    ? convertToWhitelabelURL("/profile/leads", isDemoSubdomain, partner_uuid)
    : "/profile/leads";

  const handleEnquire = (e: any) => {
    trackEnquireNow(String(item.id), "property", name);
    if (showViewProperty || [1, 2].includes(searchPageCtaExperimentValue)) {
      handlePropertyCardClick(e, [1].includes(searchPageCtaExperimentValue));
    } else {
      (async () => {
        if (isHappyFlow && isLFOneFilled) {
          setIsLoading(true);
          await handleLeadAPICall({
            dispatch,
            inventory: item,
            isLeadFormFilled,
            inModal: true,
            openingSource: "search_inventory_enquire",
            formData: personalDetails,
            isLFOneFilled,
            filters,
            history,
            isPaymentBeforeBookform,
            isZendeskFlowAfterLead,
            isDesktop,
            redirectWithoutSuccessScreen: openLeadModalFromBookCTA,
            isWhitelabel,
            isDemoSubdomain,
            partner_uuid,
          });
          setIsLoading(false);
        }
        if (!isLFOneFilled || !isHappyFlow) {
          const leadFormDispatcher = new LeadFormDispatcher(dispatch);
          leadFormDispatcher.handleOnEnquireClick({
            data: item,
            source: "search_inventory_enquire",
            page: 1,
          });
        }
      })();

      gtagEventEmitter({
        action: "clicked_property_card_contact",
        category: page,
        label: `${item?.name}${item?.location}`,
        canonical_name: item?.canonical_name,
        // flag: enableNewEnquireAndBookingFlow,
        page,
        location: item?.location?.locality?.long_name || item?.name,
      });
      gtagDuplicateEventEmitter({
        action: "clicked_property_card_contact_newleadform",
        category: page,
        label: `${item?.name}${item?.location}`,
        canonical_name: item?.canonical_name,
        // flag: enableNewEnquireAndBookingFlow,
        page,
        location: item?.location?.locality?.long_name || item?.name,
      });
    }
  };

  const handleVisitSite = () => {
    customWindowOpen({ url: item?.source_link, target: "_blank", isExternal: true });
  };

  const handleVisitRooms = () => {
    gtagEventEmitter({
      action: `clicked_view_rooms_${page}`,
      category: page,
      label: `${item?.name}${item?.location}`,
      canonical_name: item?.canonical_name,
      page,
      location: item?.location?.locality?.long_name || item?.name,
      cta_text: getButtonText(),
    });

    const params = getPropertyFilter();
    const updatedPrams = {
      ref: page === PAGE.ACTIVITY ? "activity" : "search",
      ...params,
      ...(region.id && { regionId: region.id }),
    };
    Object.assign(updatedPrams, getIframeSourceParam(isIframe, iframeSource));
    Object.assign(updatedPrams, getIframeEventDataParam());

    let link = getLink(item, updatedPrams);

    if (isWhitelabel) {
      link = convertToWhitelabelURL(link, isDemoSubdomain, partner_uuid);
    }
    runTasks([
      isDesktop
        ? () => customWindowOpen({ url: `${link}#room_type`, target: "_blank" })
        : () => viewTransitionNavUtility(() => history.push(`${link}#room_type`), "right-to-left"),
      () => {
        const propertyPageDispatcher = new PropertyPageDispatcher(dispatch);
        propertyPageDispatcher.updateBasicDetails(item);
      },
    ]);
  };
  const isApp = useIsApp();

  const handleEnquireOrVisit: MouseEventHandler = (e: any) => {
    e.preventDefault();
    e.stopPropagation();
    if (meta?.is_partnered) {
      const inventoryId: string = item.id.toString();
      const { payment_initiated, book_form_completed, book_form_initiated, payment_completed } =
        redirection_urls;

      if (isHappyFlow && isPaymentBeforeBookform) {
        if (payment_completed[inventoryId] && book_form_completed[inventoryId])
          if (isApp) navigateTo(history, "/profile/leads");
          else customWindowOpen({ url: "/profile/leads", target: isDesktop ? "_blank" : "_self" });
        else if (payment_completed[inventoryId]) {
          gtagEventEmitter({
            action: "clicked_continue_booking_property_search_page",
            category: PAGE.SEARCH,
            label: "property_sidebar",
            page: PAGE.SEARCH,
          });
          const dataObj = book_form_initiated[inventoryId] || payment_completed[inventoryId];

          const link = `${dataObj.link}&payment_before_bookform=true`;
          if (isHappyFlowBookForm)
            viewTransitionNavUtility(
              () => history.push(link[0] === "/" ? link : `/${link}`),
              "right-to-left",
            );
          else customWindowOpen({ url: link[0] === "/" ? link : `/${link}`, target: "_self" });
        } else if (payment_initiated[inventoryId]) {
          gtagEventEmitter({
            action: "clicked_continue_payment_property_search_page",
            category: PAGE.SEARCH,
            label: "property_sidebar",
            page: PAGE.SEARCH,
          });
          const link = `${payment_initiated[inventoryId].link}&payment_before_bookform=true`;
          if (isHappyFlowBookForm)
            viewTransitionNavUtility(
              () => history.push(link[0] === "/" ? link : `/${link}`),
              "right-to-left",
            );
          else customWindowOpen({ url: link[0] === "/" ? link : `/${link}`, target: "_self" });
        } else if (isEnquired) handleVisitRooms();
        else handleEnquire(e);
      } else if (!isPaymentBeforeBookform && isHappyFlow) {
        if (payment_completed[inventoryId])
          customWindowOpen({ url: bookingsPageLink, target: isDesktop ? "_blank" : "_self" });
        else if (
          payment_initiated[inventoryId] ||
          book_form_completed[inventoryId] ||
          book_form_initiated[inventoryId]
        ) {
          if (payment_initiated[inventoryId] || book_form_initiated[inventoryId])
            gtagEventEmitter({
              action: payment_initiated[inventoryId]
                ? "clicked_continue_payment_property_search_page"
                : "clicked_continue_booking_property_search_page",
              category: PAGE.SEARCH,
              label: "property_sidebar",
              page: PAGE.SEARCH,
            });

          // link will be null incase where payment is completed once
          let link =
            payment_initiated[inventoryId]?.link ||
            book_form_completed[inventoryId]?.link ||
            book_form_initiated[inventoryId]?.link ||
            "profile/leads";
          link = !isWhitelabel ? link : convertToWhitelabelURL(link, isDemoSubdomain, partner_uuid);
          if (isHappyFlowBookForm)
            viewTransitionNavUtility(
              () => history.push(link[0] === "/" ? link : `/${link}`),
              "right-to-left",
            );
          else customWindowOpen({ url: link[0] === "/" ? link : `/${link}`, target: "_self" });
          // if (isDesktop) customWindowOpen({ url: `/${link}`, target: "_blank" });
          // else {
          //   customWindowOpen({ url: `/${link}`, target: "_self" });
          // }
        } else if (isEnquired || isWhitelabel) handleVisitRooms();
        else handleEnquire(e);
      } else handleEnquire(e);
    } else {
      handleVisitSite();
    }
  };

  const handlePropertyCardClick = (e: any, shouldNavigateRoomsSection = false) => {
    gtagEventEmitter({
      action: `${page}_property_view`,
      category: PAGE.PROFILE,
      label: `${name}${location}`,
      canonical_name,
      email_id: userData?.email,
      cta_text: getButtonText(),
    });
    e.preventDefault();
    e.stopPropagation();
    // const propertyPageDispatcher = new PropertyPageDispatcher(dispatch);
    // propertyPageDispatcher.updateBasicDetails(props);
    if (shouldNavigateRoomsSection) {
      if (!redirectionLink.endsWith("#room_type")) {
        redirectionLink = `${redirectionLink.replace(/#room_type$/, "")}#room_type`;
      }
    } else {
      redirectionLink = redirectionLink.replace(/#room_type$/, "");
    }
    if (typeof window !== undefined) {
      handleRedirectToPropertyPage({
        dispatch,
        inventory: item,
        history,
        regionId: region?.id,
        isDesktop,
        isDemoSubdomain,
        partner_uuid,
        redirectionLink,
        customWindowOpen,
      });
    }
  };

  const getButtonText = () => {
    if (meta?.is_partnered) {
      const inventoryId: string = item?.id?.toString();
      const { payment_initiated, book_form_completed, book_form_initiated, payment_completed } =
        redirection_urls;

      if (isHappyFlow && isPaymentBeforeBookform) {
        if (payment_completed[inventoryId] && book_form_completed[inventoryId])
          return t("searchpage.components.searchlistitem.bottomsection.viewbookingsbutton");
        if (payment_completed[inventoryId])
          return isApplyNowCTA
            ? t("searchpage.components.searchlistitem.bottomsection.continuetoapplybutton")
            : t("searchpage.components.searchlistitem.bottomsection.continuetobookbutton");
        if (payment_initiated[inventoryId])
          return t("searchpage.components.searchlistitem.bottomsection.continuetopaybutton");
        return isEnquired
          ? t("searchpage.components.searchlistitem.bottomsection.viewroomsbutton")
          : t(defaultCTATextWithExperiment[searchPageCtaExperimentValue]);
      }

      if (isHappyFlow && !isPaymentBeforeBookform) {
        if (payment_completed[inventoryId])
          return t("searchpage.components.searchlistitem.bottomsection.viewbookingsbutton");
        if (
          payment_initiated[inventoryId] ||
          book_form_completed[inventoryId] ||
          book_form_initiated[inventoryId]
        ) {
          return (
            t(payment_initiated[inventoryId]?.label) ||
            t(book_form_completed[inventoryId]?.label) ||
            (isApplyNowCTA
              ? t("searchpage.components.searchlistitem.bottomsection.continuetoapplybutton")
              : t(book_form_initiated[inventoryId]?.label))
          );
        }
      }

      const withExperimentText = [1, 2].includes(searchPageCtaExperimentValue)
        ? isApplyNowCTA
          ? t("Continue to Apply")
          : t("Continue to Book")
        : "";

      return (isEnquired || isWhitelabel) && isHappyFlow
        ? withExperimentText ||
            t("searchpage.components.searchlistitem.bottomsection.viewroomsbutton")
        : showViewProperty
        ? t("searchpage.components.searchlistitem.bottomsection.viewpropertybutton")
        : t(defaultCTATextWithExperiment[searchPageCtaExperimentValue]);
    }
    return t("searchpage.components.searchlistitem.bottomsection.visitsitebutton");
  };

  const PriceComp = () =>
    item?.slashed_available_price && showSlashPricing ? (
      <div className={styles.slashedPriceContainer}>
        <div className={styles.priceSection}>
          <div>
            <span className={styles.from}>
              {config.IS_CHINA_APP
                ? ""
                : t("searchpage.components.searchlistitem.bottomsection.fromtext")}
            </span>
            {!config.IS_CHINA_APP && <span className={styles.slashPricing}>{pricingPrice}</span>}
          </div>
          <div>
            <span className={styles.price} data-testid="searchPage-inventoryCard-price">
              {config.IS_CHINA_APP && <span className={styles.slashPricing}>{pricingPrice}</span>}{" "}
              {getPricingCurrency(pricing.currency)}
              {item.slashed_available_price}
            </span>
            <span className={styles.duration}>/ {t(pricingDuration)}</span>
          </div>
        </div>
      </div>
    ) : (
      <>
        <div className={styles.slashedPriceContainer}>
          <div className={styles.priceSection}>
            <div>
              <span className={styles.from}>
                {config.IS_CHINA_APP
                  ? ""
                  : t("searchpage.components.searchlistitem.bottomsection.fromtext")}
              </span>
            </div>
            <div>
              <span className={styles.price} data-testid="searchPage-inventoryCard-price">
                {pricingPrice}
              </span>
              <span className={styles.duration}>/ {t(pricingDuration)}</span>
            </div>
          </div>
        </div>
      </>
    );

  return (
    <div
      className={formatClassNames(styles.container, isDesktop && styles.desktop, wrapperClassName)}
    >
      {showPropertyCardTag && (
        <PropertyTags inventory={item} showCashbackValue className={styles.propertyTag} />
      )}
      {PriceComp()}
      {showEnquireButton && (
        <div
          className={formatClassNames(isDesktop ? styles.cta : styles.mobileCta, className)}
          ref={wrapRef}
        >
          {!isShortlistPage && (
            <Button
              // {...(isDesktop && {
              //   RightIcon: () => (
              //     <ArrowIcon
              //       rotate={270}
              //       {...(isWhitelabel && { stroke: whitelabelColors?.cta_text_color })}
              //     />
              //   ),
              // })}
              variant={isWhitelabel ? "whiteLabel_primary" : "primary"}
              onClick={handleEnquireOrVisit}
              {...(meta?.is_partnered && isLFOneFilled && isHappyFlow && { showLoader: isLoading })}
              dataTestId={`searchPage-inventoryCard-${getButtonText()}-button`}
              {...(!isDesktop && { className: styles.buttonPadding })}
              className={formatClassNames(getButtonText() !== "Enquire" && styles.smallText)}
            >
              {searchPageCtaExperimentValue === -1 ? (
                <Skeleton height="12px" width="128px" addBorderRadius />
              ) : (
                getButtonText()
              )}
            </Button>
          )}
          {isShortlistPage && (
            <Button
              {...(isDesktop && {
                RightIcon: () => (
                  <ArrowIcon
                    rotate={270}
                    {...(isWhitelabel && { stroke: whitelabelColors?.cta_text_color })}
                  />
                ),
              })}
              variant={isWhitelabel ? "whiteLabel_primary" : "primary"}
              onClick={handlePropertyCardClick}
              dataTestId="shortlistPage-inventoryCard-enquire-button"
              {...(!isDesktop && { className: styles.buttonPadding })}
            >
              {t("searchpage.components.searchlistitem.bottomsection.shortlistviewbutton")}
            </Button>
          )}
        </div>
      )}
    </div>
  );
};

export default wrapperHOC(BottomSection, {
  componentName: "BottomSection-SearchListItemDesktop-SearchPage",
  showForChina: true,
});
